import {FormElement} from "./form-element.js";
import {OptionTools} from "./option-tools.js";
import {OptionElement} from "./option-element.js";

/**
 * The `SelectElement` class represents a form element of type "select".
 * It extends functionality from the `FormElement` class and provides methods
 * to dynamically generate, update, and manipulate a selection dropdown component.
 */
export class SelectElement extends FormElement {
    options;
    select;

    componentTag = "select";
    optionElements;

    /**
     * Constructor for initializing the class with form data, parent element, and collection.
     *
     * @param {Object} formData - The data object containing form-related information.
     * @param {HTMLElement} parentElement - The parent HTML element where the form will be rendered.
     * @param {array} collection - The collection of data items related to the form.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);

        this.optionElements = [];
    }

    /**
     * Updates the attributes of the given component by calling the parent class's attributes method
     * and setting the name property of the component to the value of this instance's dataField.
     *
     * @param {Object} component - The component whose attributes are being updated.
     * @return {void} Does not return a value.
     */
    attributes(component) {
        super.attributes(component);
        component.name = this.dataField;
    }

    /**
     * Initializes the component by calling the parent component's method
     * and setting additional options.
     *
     * @return {Object} The initialized component instance.
     */
    component() {
        this.select = super.component();
        this.setOptions();

        return this.select;
    }

    getPredecessor() {
        const elementName = this.dependsOn ?? this.options?.dependsOn;
        if (elementName == null || elementName.length === 0 || this.collection?.elements == null) return;
        return this.collection.elements[elementName];
    }

    getSuccessorValue() {
        if (this.domComponent == null) return
        const selectedOptions = this.domComponent.selectedOptions;
        if (selectedOptions == null || selectedOptions.length === 0) return;

        return selectedOptions[0]?.dataset?.value;
    }

    /**
     * Builds the options list for the Select box
     *
     * @param select The Select to update the options for
     */
    setOptions() {
        OptionTools.clearOptions(this.optionElements);

        const selectOptions = OptionTools.buildOptions(this, this.options, this.formData);
        if (selectOptions == null) return;

        let resetValue = true;
        selectOptions.forEach(option => {
            const optionElement = this.setOption(option.text, option.value, option.style, option.dataValue);
            if (optionElement.selected === true && (optionElement.value ?? "").length > 0) {
                resetValue = false;
            }

            this.optionElements.push(optionElement);
        });

        if (resetValue) this.setValue(null);

        const values = this.optionElements?.filter(item =>
            item.value !== null && (typeof item.value === "number" || (typeof item.value === "string" && item.value.trim() !== ""))
        );

        const hasFormDataValue = this.formData?.[this.field] !== undefined && this.formData?.[this.field] !== "";
        const defaultValue = this.default; 
        const hasDefault = defaultValue !== undefined && defaultValue !== null;

        if (!hasFormDataValue && hasDefault) {
            this.setValue(defaultValue);
            const match = values.find(opt => opt.value === defaultValue || opt.text === defaultValue); //permit using either the key or the value
            if (match) match.selected = true; 
            this.ignorePlaceholder = true; //always ignore the placeholder if a default is used 
        }

        if (values.length === 1) {
            if (this.optionElements.length === 2 && (this.optionElements[0]?.value ?? "") === "") {
                OptionTools.clearOption(this.optionElements.shift());
            }
            this.setValue(values[0].value);
            values[0].selected = true;
        }

        //if ignoring the placeholder, this sets the selected option to be the first valid value 
        //if the placeholder has been removed, this will be the first value. otherwise, it will be the second in the list.
        if (this.ignorePlaceholder && !hasDefault) {
            if (values[0].value) {
                this.setValue(values[0].value);
                values[0].selected = true; 
            } else {
                this.setValue(values[1].value);
                values[1].selected = true;
            }
        }
        this.setPlaceholderStyle();
        this.emitChangeNotification();
    }

    /**
     * Sets the visibility of an element by calling the parent class's setVisibility method
     * and passing in a function to handle element display.
     *
     * @return {void} Returns nothing.
     */
    setVisibility() {
        super.setVisibility(() => this.showElement());
    }

    /**
     * Determines whether an element should be shown based on the `postOptionsFilter`
     * and conditions related to filter dependencies and the number of option elements.
     *
     * @return {boolean} Returns `true` if the element should be shown, otherwise `false`.
     */
    showElement() {
        const singleHide = this.postOptionsFilter?.singleHide;
        if (singleHide == null || typeof singleHide !== "object" || Object.keys(singleHide).length === 0) {
            return true;
        }

        const key = OptionTools.postOptionsFilterDepends(this);
        const checkSingle = key != null && (this.postOptionsFilter.singleHide[key] ?? false);

        return !checkSingle || this.optionElements.length > 1 || (this.optionElements?.[0].value ?? "") === "";
    }

    /**
     * Creates the Options tag
     * @param text
     * @param value
     * @param style
     * @param dataValue
     */
    setOption(text, value, style = null, dataValue = null) {
        const option = Object.assign(new OptionElement(this.formData, this.select, this.collection), {text, value, style, dataValue});
        option.render();

        option.selected = this.dataValue === (value ?? text);

        return option;
    }

    /**
     * Adds an attribute to style the select box if displaying a placeholder value.
     */
    setPlaceholderStyle() {
        if (!this.select) return;

        if (this.select.value === "") this.select.classList.add("has-placeholder");
        else this.select.classList.remove("has-placeholder");
    }

    /**
     * Updates options dynamically and emits any changes made by a selection
     */
    changeEvent(data) {
        if (!OptionTools.isDynamicOptions(this.options) || this.options.dependsOn !== data.field) {
            return;
        }

        this.setOptions();
    }

    /**
     * Override the defaultListeners in order to set the placeholder styling dynamically.
     * This ensures that when a placeholder is selected (initial load), it is greyed out. 
     * When a valid option is selected, the placeholder styling is removed.
     */
    defaultListeners(component) {
        super.defaultListeners(component);
        component.addEventListener('change', event => {
            this.setPlaceholderStyle();
        });
    }

    /**
     * Adds a postRender event to ensure that logic that depends on the selected value always fires. 
     */
    postRender() {
        if (!this.select) {
            return;
        }

        const hasUserSuppliedValue = this.formData?.[this.field] !== undefined && this.formData?.[this.field] !== "";
        const hasDefault = this.default !== undefined && this.default !== null;

        if ((hasUserSuppliedValue || hasDefault) && this.select.value !== "") {
            //delay until render is done
            setTimeout(() => {
                const event = new Event("change", {bubbles: true});
                this.select.dispatchEvent(event);
            }, 0);
        }
    }
}
